import {getDefaultRpcInstance} from 'src/internal/rpc/defaultRpc';
import {v4 as uuid} from 'uuid';

import type {ImperativeMetrics, ObservabilityConfig} from 'src/types';

const rpcMethodMap = {
  increment: 'metricsIncrement',
  gauge: 'metricsGauge',
  set: 'metricsSet',
} as const;

const method = <TMethod extends keyof ImperativeMetrics>(
  name: TMethod,
): ImperativeMetrics[TMethod] => {
  return (config: ObservabilityConfig | null, ...params: [...any]): void => {
    if (!config || config.metrics?.disabled) {
      return;
    }

    if (config._tmp_dualReporting) {
      const index = name === 'increment' ? 1 : 2;

      params[index] = {
        ...params[index],
        _tdr_id: uuid(),
      };
    }

    const injectedMethod = config._internal_observability?.metrics?.[name];

    if (injectedMethod) {
      // @ts-expect-error Passed in `params` is a union of tuples.
      injectedMethod(config, ...params);

      if (!config._tmp_dualReporting) {
        return;
      }
    }

    const rpcClient = config.rpc ?? getDefaultRpcInstance().client;
    const rpcMethod = rpcMethodMap[name];

    const paramsWithPotentialDualReporting = config._tmp_dualReporting
      ? [`_tdr.${params[0]}`, ...params.slice(1)]
      : params;

    rpcClient.call<any>(rpcMethod, config, ...paramsWithPotentialDualReporting);
  };
};

/**
 * @see https://sail.stripe.me/observability/api/useMetrics#increment
 */
export const increment = method('increment');

/**
 * @see https://sail.stripe.me/observability/api/useMetrics#gauge
 */
export const gauge = method('gauge');

/**
 * @see https://sail.stripe.me/observability/api/useMetrics#set
 */
export const set = method('set');
